'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import AppLogo from '@/components/ui/AppLogo';
import { Eye, EyeOff, Copy, Check, ArrowRight, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

interface LoginForm {
  email: string;
  password: string;
  remember: boolean;
}

interface SignupForm {
  name: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
}

const demoCredentials = [
  { key: 'cred-director', role: 'Director', email: 'ahmad.farhan@safaruna.id', password: 'Director@2026', color: 'text-amber-400' },
  { key: 'cred-sales', role: 'Sales', email: 'rizky.sales@safaruna.id', password: 'Sales@2026', color: 'text-emerald-400' },
  { key: 'cred-concierge', role: 'Concierge', email: 'siti.concierge@safaruna.id', password: 'Concierge@2026', color: 'text-blue-400' },
  { key: 'cred-finance', role: 'Finance', email: 'dewi.finance@safaruna.id', password: 'Finance@2026', color: 'text-purple-400' },
];

const pillars = [
  { key: 'pillar-personal', icon: '✦', text: 'Perjalanan dirancang sesuai kebutuhan Anda' },
  { key: 'pillar-transparent', icon: '◈', text: 'Harga transparan, tanpa biaya tersembunyi' },
  { key: 'pillar-concierge', icon: '◉', text: 'Pendampingan personal dari awal hingga selesai' },
];

export default function LoginPageClient() {
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const loginForm = useForm<LoginForm>({ defaultValues: { email: '', password: '', remember: false } });
  const signupForm = useForm<SignupForm>();

  const handleLoginSubmit = loginForm.handleSubmit(async (data) => {
    setIsLoading(true);
    // Backend integration point: POST /api/auth/login
    await new Promise((r) => setTimeout(r, 1200));
    const validCred = demoCredentials.find(c => c.email === data.email && c.password === data.password);
    if (!validCred) {
      loginForm.setError('email', { message: 'Kredensial tidak valid — gunakan akun demo di bawah untuk masuk' });
      setIsLoading(false);
      return;
    }
    toast.success(`Selamat datang, ${validCred.role}!`);
    window.location.href = '/management-dashboard';
    setIsLoading(false);
  });

  const handleSignupSubmit = signupForm.handleSubmit(async (data) => {
    setIsLoading(true);
    // Backend integration point: POST /api/auth/register
    await new Promise((r) => setTimeout(r, 1200));
    toast.success('Akun berhasil dibuat! Silakan masuk.');
    setMode('login');
    setIsLoading(false);
  });

  const fillCredential = (cred: typeof demoCredentials[0]) => {
    loginForm.setValue('email', cred.email);
    loginForm.setValue('password', cred.password);
    loginForm.clearErrors();
  };

  const copyToClipboard = async (text: string, fieldKey: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(fieldKey);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--mgmt-bg)' }}>
      {/* Left Brand Panel */}
      <div className="hidden lg:flex lg:w-[45%] xl:w-[42%] flex-col relative overflow-hidden bg-emerald-gradient">
        <div className="absolute inset-0 pattern-arabesque opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/80 to-transparent" />

        <div className="relative z-10 flex flex-col h-full p-10 xl:p-14">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <AppLogo size={40} />
            <div>
              <span className="font-bold text-xl text-white tracking-tight block">SAFARUNA</span>
              <span className="text-xs text-white/60 tracking-widest uppercase">Private Umrah Concierge</span>
            </div>
          </div>

          {/* Center Content */}
          <div className="flex-1 flex flex-col justify-center">
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-white/70 text-xs font-medium mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                Management System
              </div>
              <h2 className="text-3xl xl:text-4xl font-bold text-white leading-tight mb-4">
                Umrah yang dirancang<br />
                <span className="text-gradient-gold">sesuai kebutuhan Anda.</span>
              </h2>
              <p className="text-white/60 text-sm leading-relaxed max-w-sm">
                Platform manajemen perjalanan Umrah privat — dari lead hingga completed journey tanpa redundant input.
              </p>
            </div>

            <div className="space-y-4">
              {pillars.map((p) => (
                <div key={p.key} className="flex items-center gap-3">
                  <span className="text-accent text-lg font-bold">{p.icon}</span>
                  <p className="text-white/70 text-sm">{p.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 p-4 rounded-2xl bg-white/5 border border-white/10">
            {[
              { key: 'stat-bookings', value: '1,247', label: 'Booking' },
              { key: 'stat-pax', value: '4,891', label: 'Jamaah' },
              { key: 'stat-rating', value: '4.9★', label: 'Rating' },
            ].map((s) => (
              <div key={s.key} className="text-center">
                <p className="text-white font-bold text-lg font-mono-data">{s.value}</p>
                <p className="text-white/50 text-xs mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Form Panel */}
      <div className="flex-1 flex flex-col justify-center px-6 py-10 lg:px-12 xl:px-16 overflow-y-auto scrollbar-thin">
        {/* Mobile Logo */}
        <div className="lg:hidden flex items-center gap-2.5 mb-8">
          <AppLogo size={32} />
          <span className="font-bold text-lg text-mgmt">SAFARUNA</span>
        </div>

        <div className="max-w-md w-full mx-auto">
          {/* Tabs */}
          <div className="flex rounded-xl p-1 mb-8" style={{ backgroundColor: 'var(--mgmt-surface)' }}>
            {(['login', 'signup'] as const).map((tab) => (
              <button
                key={`tab-${tab}`}
                onClick={() => setMode(tab)}
                className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                  mode === tab
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-mgmt-muted hover:text-mgmt'
                }`}
              >
                {tab === 'login' ? 'Masuk' : 'Daftar'}
              </button>
            ))}
          </div>

          {mode === 'login' ? (
            <form onSubmit={handleLoginSubmit} className="space-y-5">
              <div>
                <label className="block text-xs font-semibold text-mgmt-muted uppercase tracking-wider mb-1.5">
                  Email
                </label>
                <input
                  type="email"
                  placeholder="nama@safaruna.id"
                  className="mgmt-input"
                  {...loginForm.register('email', { required: 'Email wajib diisi' })}
                />
                {loginForm.formState.errors.email && (
                  <p className="text-red-400 text-xs mt-1.5">{loginForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-mgmt-muted uppercase tracking-wider mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    className="mgmt-input pr-12"
                    {...loginForm.register('password', { required: 'Password wajib diisi' })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-mgmt-muted hover:text-mgmt transition-colors"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="text-red-400 text-xs mt-1.5">{loginForm.formState.errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded accent-primary"
                    {...loginForm.register('remember')}
                  />
                  <span className="text-xs text-mgmt-muted">Ingat saya</span>
                </label>
                <button type="button" className="text-xs text-accent hover:underline">
                  Lupa password?
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full justify-center py-3.5 text-sm"
                style={{ backgroundColor: 'var(--primary)' }}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Memverifikasi...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Masuk ke Dashboard
                    <ArrowRight size={16} />
                  </span>
                )}
              </button>

              <div className="relative flex items-center gap-3">
                <div className="flex-1 h-px bg-mgmt-border" style={{ backgroundColor: 'var(--mgmt-border)' }} />
                <span className="text-xs text-mgmt-muted">atau</span>
                <div className="flex-1 h-px" style={{ backgroundColor: 'var(--mgmt-border)' }} />
              </div>

              <a
                href="https://wa.me/6281234567890"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl border text-sm font-medium transition-all duration-150 hover:opacity-80 active:scale-95"
                style={{ borderColor: 'var(--mgmt-border)', color: 'var(--mgmt-text-muted)' }}
              >
                <MessageCircle size={16} className="text-green-400" />
                Masuk via WhatsApp
              </a>
            </form>
          ) : (
            <form onSubmit={handleSignupSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-mgmt-muted uppercase tracking-wider mb-1.5">Nama Lengkap</label>
                <input
                  type="text"
                  placeholder="Ahmad Farhan"
                  className="mgmt-input"
                  {...signupForm.register('name', { required: 'Nama wajib diisi' })}
                />
                {signupForm.formState.errors.name && (
                  <p className="text-red-400 text-xs mt-1.5">{signupForm.formState.errors.name.message}</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-mgmt-muted uppercase tracking-wider mb-1.5">Email</label>
                <input type="email" placeholder="nama@email.com" className="mgmt-input"
                  {...signupForm.register('email', { required: 'Email wajib diisi' })} />
                {signupForm.formState.errors.email && (
                  <p className="text-red-400 text-xs mt-1.5">{signupForm.formState.errors.email.message}</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-mgmt-muted uppercase tracking-wider mb-1.5">Nomor WhatsApp</label>
                <input type="tel" placeholder="+62 812 3456 7890" className="mgmt-input"
                  {...signupForm.register('phone', { required: 'Nomor WhatsApp wajib diisi' })} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-mgmt-muted uppercase tracking-wider mb-1.5">Password</label>
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} placeholder="Min. 8 karakter" className="mgmt-input pr-12"
                    {...signupForm.register('password', { required: 'Password wajib diisi', minLength: { value: 8, message: 'Minimal 8 karakter' } })} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-mgmt-muted hover:text-mgmt transition-colors">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {signupForm.formState.errors.password && (
                  <p className="text-red-400 text-xs mt-1.5">{signupForm.formState.errors.password.message}</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-mgmt-muted uppercase tracking-wider mb-1.5">Konfirmasi Password</label>
                <div className="relative">
                  <input type={showConfirmPassword ? 'text' : 'password'} placeholder="Ulangi password" className="mgmt-input pr-12"
                    {...signupForm.register('confirmPassword', { required: 'Konfirmasi password wajib diisi' })} />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-mgmt-muted hover:text-mgmt transition-colors">
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 rounded mt-0.5 accent-primary"
                  {...signupForm.register('terms', { required: true })} />
                <span className="text-xs text-mgmt-muted leading-relaxed">
                  Saya setuju dengan <button type="button" className="text-accent hover:underline">Syarat & Ketentuan</button> dan{' '}
                  <button type="button" className="text-accent hover:underline">Kebijakan Privasi</button> SAFARUNA
                </span>
              </label>
              <button type="submit" disabled={isLoading} className="btn-primary w-full justify-center py-3.5 text-sm" style={{ backgroundColor: 'var(--primary)' }}>
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Membuat akun...
                  </span>
                ) : 'Buat Akun'}
              </button>
            </form>
          )}

          {/* Demo Credentials */}
          <div className="mt-8 rounded-2xl overflow-hidden border" style={{ borderColor: 'var(--mgmt-border)' }}>
            <div className="px-4 py-3 flex items-center justify-between" style={{ backgroundColor: 'var(--mgmt-surface)' }}>
              <p className="text-xs font-semibold text-mgmt-muted uppercase tracking-wider">Akun Demo</p>
              <span className="text-[10px] text-mgmt-muted">Klik baris untuk isi otomatis</span>
            </div>
            <div className="divide-y" style={{ borderColor: 'var(--mgmt-border)' }}>
              {demoCredentials.map((cred) => (
                <div
                  key={cred.key}
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors hover:opacity-80"
                  style={{ backgroundColor: 'var(--mgmt-card)' }}
                  onClick={() => { fillCredential(cred); setMode('login'); }}
                >
                  <span className={`text-xs font-bold w-20 flex-shrink-0 ${cred.color}`}>{cred.role}</span>
                  <span className="text-xs text-mgmt-muted flex-1 truncate font-mono-data">{cred.email}</span>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); copyToClipboard(cred.email, `${cred.key}-email`); }}
                      className="p-1 rounded text-mgmt-muted hover:text-mgmt transition-colors"
                      title="Salin email"
                    >
                      {copiedField === `${cred.key}-email` ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="text-center text-xs text-mgmt-muted mt-6">
            Customer? Akses{' '}
            <Link href="/" className="text-accent hover:underline">
              portal pelanggan
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}